package com.elan.edubridge.shared.domain.model

import kotlinx.serialization.Serializable

/**
 * `edubridge_academies/{id}`. 출결 판정에 필요한 학원 설정을 함께 들고 있다.
 *
 * 시각 관련 값이 전부 학원 단위인 이유: KST/UTC 경계에서 일별 집계가 어긋나면
 * 00:00~09:00 수업이 전날로 집계된다(R12). `serviceDate`는 항상 [timeZone] 기준으로 계산한다.
 */
@Serializable
data class AcademyData(
    val id: String,
    val name: String,
    val description: String,
    val phone: String,
    val address: String,
    val studentCount: Int = 0,
    val teacherCount: Int = 0,
    val timeZone: String = "Asia/Seoul",
    /** 수업 시작 후 이 시간을 넘겨 등원하면 LATE(§3.4-7, Q11). */
    val lateThresholdMinutes: Int = 10,
    /** QR 회전 주기. 짧을수록 안전하지만 태블릿 네트워크가 불안하면 반복 실패한다(Q8). 최소 10초. */
    val qrRotationSeconds: Int = 20,
    /** 스캔~도달 지연 흡수 창(§3.4-3). */
    val qrGraceSeconds: Int = 15,
    /** 같은 학생의 연속 스캔 차단 간격(§3.4-6). */
    val minScanIntervalSeconds: Int = 60,
)

/** `edubridge_academies/{academyId}/classes/{id}`. */
@Serializable
data class ClassData(
    val id: String,
    val academyId: String,
    val className: String,
    val subject: String,
    val teacherUserId: String? = null,
    val teacher: String,
    val schedule: ClassSchedule = ClassSchedule(),
    val studentCount: Int = 0,
)

/**
 * `edubridge_academies/{academyId}/students/{id}`.
 *
 * 편집 경로는 `updateStudent` 콜러블뿐이다(rules `allow write: if false` 유지).
 * [guardianContacts]는 개인정보이므로 변경이 감사 로그 대상이다(§2.5).
 */
@Serializable
data class StudentData(
    val id: String,
    val academyId: String,
    /** 학생 계정 연결 전이면 null. */
    val userId: String? = null,
    val name: String,
    val grade: String,
    val phone: String? = null,
    val guardianContacts: List<GuardianContact> = emptyList(),
)

/**
 * `edubridge_academies/{academyId}/notices/{id}`.
 *
 * [date]는 발행 시각(ISO-8601)이자 목록 정렬 기준이다 — `notices.ts`가 생성 시각을 그대로 쓴다.
 *
 * [status]가 [ContentStatus.DELETED]면 툼스톤이다. 삭제해도 문서는 남고 제목·본문만 비워지므로
 * 목록을 그리는 쪽은 ACTIVE만 봐야 한다(필터는 `EduConnectRepositoryImpl`에 있다).
 */
@Serializable
data class NoticeData(
    val id: String,
    val academyId: String,
    val title: String,
    val date: String,
    /** 평문. 서식 공지에서는 서버가 [contentHtml]에서 뽑아낸 것 — 목록 미리보기·푸시·서식을 못 그리는 화면이 쓴다. */
    val content: String,
    /**
     * 서식 본문(HTML). 서버(`functions/src/common/html.ts`)가 허용 태그만 남겨 저장하므로 클라이언트는
     * 그대로 그려도 된다 — 살균은 서버 한 곳에서만 한다. 에디터가 생기기 전 문서와 평문으로 고친 공지는 null.
     */
    val contentHtml: String? = null,
    val status: ContentStatus = ContentStatus.ACTIVE,
    val authorUid: String? = null,
    /** 작성 시점의 표시 이름 스냅샷. 계정에 이름이 없으면 서버가 null로 쓴다. */
    val authorName: String? = null,
)

/**
 * 과제 종류. 문자열 값은 `assignments.ts`·Firestore 문서와의 계약이다.
 *
 * 이 필드가 생기기 전(2026-09-08) 문서에는 값이 없다 — 매퍼가 [NOTICE]로 읽으므로
 * 백필하지 않는다.
 */
@Serializable
enum class AssignmentKind { NOTICE, PROBLEM_BANK }

/**
 * 문제 은행 과제의 **출제 조건**. 문제 본문도 정답도 여기 없다 —
 * 문제 은행은 EduMint 소유이고, EduBridge 가 갖는 것은 어느 묶음을 몇 문항 풀라는 지시뿐이다.
 *
 * 필드 값이 한국어 리터럴인 이유는 [combinationKey] 때문이다: 이 키가 EduMint 문제 은행
 * 청크의 키라서 `{grade}_{subject}_{difficulty}[_{questionType}]` 가 바이트 단위로 같아야 한다.
 * 그래서 서버(`assignments.ts`)가 조합해 저장하고, 클라이언트는 만들지 않는다.
 *
 * [questionType]은 수학에 없는 축이라 빈 문자열이다(키에서도 그 마디가 빠진다).
 */
@Serializable
data class ProblemBankSpec(
    /** "국어" | "수학". */
    val subject: String,
    /** "초1".."고3". */
    val grade: String,
    /** "쉬움" | "보통" | "어려움". */
    val difficulty: String,
    /** "객관식" | "OX" — 수학이면 빈 문자열. */
    val questionType: String = "",
    val questionCount: Int,
    val combinationKey: String,
)

/**
 * `edubridge_academies/{academyId}/assignments/{id}`.
 *
 * [kind]가 [AssignmentKind.PROBLEM_BANK]면 [problemBank]가 채워져 있고, 학생 앱이 그
 * 조건으로 문항을 뽑아 풀고 `edubridge-submitAssignment`로 결과를 보낸다([AssignmentSubmission]).
 *
 * [status]가 [ContentStatus.DELETED]면 툼스톤이다 — 공지와 같은 규약으로 제목이 비워지고,
 * 목록 필터는 `EduConnectRepositoryImpl`에서 끝난다.
 */
@Serializable
data class Assignment(
    val id: String,
    val academyId: String,
    val classId: String? = null,
    val title: String,
    val teacher: String,
    val dueDate: String,
    val completed: Boolean = false,
    val kind: AssignmentKind = AssignmentKind.NOTICE,
    val problemBank: ProblemBankSpec? = null,
    /**
     * 학원이 올린 자료(PDF·사진). 게시글 첨부와 **같은 모양**이라 [PostAttachment]를 그대로 쓴다 —
     * 서버도 한 모듈(`common/attachments.ts`)이 두 문서의 첨부를 함께 다룬다.
     *
     * 목록이 자료 유무·개수를 그리는 데 추가 호출이 필요 없도록 문서 안에 인라인돼 있고,
     * 실제 파일을 여는 것은 별개다 — `edubridge-getAssignmentAttachmentUrl`로
     * 그때그때 5분짜리 서명 URL을 받는다([AssignmentAttachmentTicket]).
     */
    val attachments: List<PostAttachment> = emptyList(),
    val status: ContentStatus = ContentStatus.ACTIVE,
)

/**
 * `getAssignmentAttachmentUrl` 결과 — 5분짜리 V4 서명 GET URL.
 *
 * 매번 새로 발급받아야 한다. 삭제된 과제는 자료 목록이 비워지므로 `NOT_FOUND`로 돌아온다.
 * 게시판의 [PostAttachmentDownloadTicket]과 필드가 같지만 가리키는 문서가 달라 따로 둔다 —
 * `postId` 칸에 assignmentId를 담는 것이 이 계약에서 가장 나쁜 선택이다.
 */
@Serializable
data class AssignmentAttachmentTicket(
    val attachmentId: String? = null,
    val academyId: String? = null,
    val assignmentId: String? = null,
    val fileName: String? = null,
    val contentType: AttachmentContentType? = null,
    val sizeBytes: Int = 0,
    val objectPath: String? = null,
    val downloadUrl: String? = null,
    /** ISO-8601. 발급 후 5분. */
    val expiresAt: String? = null,
    val failure: CommandFailure? = null,
    val errorMessage: String? = null,
) {
    val isSuccess: Boolean get() = failure == null && downloadUrl != null
}

/** 제출 경로. 문자열 값은 `assignments.ts`와의 계약이다. */
@Serializable
enum class SubmissionSource { EDUMINT, EDUBRIDGE }

/**
 * `edubridge_academies/{academyId}/assignments/{assignmentId}/submissions/{studentId}`.
 *
 * 채점은 문제를 가진 쪽(EduMint 학생 앱)이 하고, 서버는 그 결과를 **어느 학생의 것인지**만
 * 정해 기록한다. 문서 id 가 [studentId]인 이유도 같다 — 계정([studentUserId])은 다시 연결될 수
 * 있지만 결과가 붙어야 할 곳은 명부의 그 학생이다.
 *
 * 재제출은 같은 문서를 덮어쓰고 [attempts]만 늘린다. [firstSubmittedAt]은 그때도 그대로다.
 */
@Serializable
data class AssignmentSubmission(
    val assignmentId: String,
    val academyId: String,
    val studentId: String,
    val studentUserId: String,
    val studentName: String,
    val correct: Int,
    val total: Int,
    val scorePercent: Int,
    val elapsedSeconds: Int = 0,
    val submittedAt: String,
    val firstSubmittedAt: String = submittedAt,
    val attempts: Int = 1,
    val source: SubmissionSource = SubmissionSource.EDUMINT,
)
